import { useState, useEffect } from "react";

import { Sparkles, Loader2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Markdown } from "@/components/ui/markdown";
import { toast } from "@/components/ui/sonner";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";

interface MomentumSummaryProps {
  projectId: string;
}

export const MomentumSummary = ({ projectId }: MomentumSummaryProps) => {
  const { execute: invoke, isLoading } = useEdgeFunction({ auth: true });
  const [summary, setSummary] = useState<{
    summary: string;
    sessionName: string;
    sessionId: string;
    lastUpdated: string;
  } | null>(null);
  const [extractedNextStep, setExtractedNextStep] = useState<string>("");
  const navigate = useNavigate();

  // Extract the first actionable next step from the summary
  useEffect(() => {
    if (summary?.summary) {
      // Look for common patterns in next steps sections
      const nextStepsPatterns = [
        /next steps?:?\s*\n[-•*]\s*(.+?)(?:\n|$)/i,
        /suggested? (?:next )?(?:step|action)s?:?\s*\n[-•*]\s*(.+?)(?:\n|$)/i,
        /action items?:?\s*\n[-•*]\s*(.+?)(?:\n|$)/i,
        /to do:?\s*\n[-•*]\s*(.+?)(?:\n|$)/i,
      ];

      for (const pattern of nextStepsPatterns) {
        const match = summary.summary.match(pattern);
        if (match && match[1]) {
          setExtractedNextStep(match[1].trim());
          break;
        }
      }
    }
  }, [summary]);

  const generateSummary = async () => {
    const { data, error } = await invoke("generate-momentum-summary", {
      body: { projectId },
      showErrorToast: true,
    });

    if (error) return;

    setSummary(data);
    toast.success("Summary generated!");
  };

  const handleContinueSession = () => {
    if (summary?.sessionId) {
      // Navigate with the next step pre-filled
      navigate(`/sessions/${summary.sessionId}`, {
        state: { prefillMessage: extractedNextStep }
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Where was I?
            </CardTitle>
            <CardDescription>
              Get a quick recap of your last session and suggested next steps
            </CardDescription>
          </div>
          <Button
            onClick={generateSummary}
            disabled={isLoading}
            variant="default"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Summary
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      {summary && (
        <CardContent>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              From session: <span className="font-medium">{summary.sessionName}</span>
            </div>
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <Markdown content={summary.summary} />
            </div>
            <div className="flex justify-end pt-4">
              <Button onClick={handleContinueSession} variant="outline">
                Continue Session
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};
